import { useApolloClient } from "@apollo/client";
import { useEffect, useRef, useState } from "react";

export const FILE_IMPORT_POLL_INTERVAL_MS = 5000;
export const FILE_IMPORT_MAX_POLL_MS = 10 * 60 * 1000;
const MAX_CONSECUTIVE_POLL_FAILURES = 3;
const ACTIVE_STATUSES = ["CREATED", "PROCESSING"];

/**
 * Maps an upload record to the label shown to the user.
 */
export const getFileImportStatusLabel = (upload) => {
  switch (upload?.uploadStatus) {
    case "CREATED":
      return upload.fileSizeBytes ? "Queued" : "Awaiting upload";
    case "PROCESSING":
      return "Processing";
    case "COMPLETED":
      return "Completed";
    case "FAILED":
      return "Failed";
    default:
      return "";
  }
};

/**
 * Shared tracker for file import status. Works with any status query that
 * takes `variables` (e.g. getSegmentFileUpload) and resolves to a record with
 * uploadStatus. Polls with fetchPolicy "network-only" every 5 seconds while the
 * status is CREATED or PROCESSING; stops on COMPLETED/FAILED, on unmount, or
 * after the cap (then `timedOut` is true).
 *
 * @param {Object} options
 * @param {Object} options.query - gql status query
 * @param {string} options.dataKey - key of the record in the query result
 * @param {Object} [options.variables] - query variables (must include the uploadId); tracking is idle when null
 * @param {Function} [options.onTerminal] - called once on COMPLETED/FAILED with the record and `{ sawActive }`,
 *   where sawActive is true when an earlier poll saw it CREATED/PROCESSING (false when it was already finished)
 */
export function useFileImportStatus({ query, dataKey, variables, onTerminal }) {
  const client = useApolloClient();
  const [upload, setUpload] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const [error, setError] = useState(null);
  const onTerminalRef = useRef(onTerminal);
  onTerminalRef.current = onTerminal;

  const variablesKey = variables ? JSON.stringify(variables) : null;

  useEffect(() => {
    setUpload(null);
    setTimedOut(false);
    setError(null);
    if (!variablesKey) return undefined;

    let cancelled = false;
    let timer;
    let failures = 0;
    let sawActive = false;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const { data } = await client.query({
          query,
          variables: JSON.parse(variablesKey),
          fetchPolicy: "network-only",
        });
        if (cancelled) return;
        failures = 0;
        const record = data?.[dataKey];
        setUpload(record || null);
        if (record && ACTIVE_STATUSES.includes(record.uploadStatus)) {
          sawActive = true;
        } else if (record) {
          onTerminalRef.current?.(record, { sawActive });
          return;
        }
      } catch (e) {
        if (cancelled) return;
        failures += 1;
        if (failures >= MAX_CONSECUTIVE_POLL_FAILURES) {
          setError(e);
          return;
        }
      }
      if (Date.now() - startedAt >= FILE_IMPORT_MAX_POLL_MS) {
        setTimedOut(true);
        return;
      }
      timer = setTimeout(poll, FILE_IMPORT_POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [client, query, dataKey, variablesKey]);

  return { upload, timedOut, error };
}
